// =============================
// src/pages/materials/request/Request_new.jsx
// =============================
import { useState, useMemo, useEffect, useCallback } from "react";
import { v4 as uuid } from "uuid";
import { useParams } from "react-router-dom";

import FormContentManager from "../../../components/common/form/FormContentManager";
import TableCell from "../../../components/common/ui/TableCell";
import PhotoBoardSelect from "../../../components/const/Photo/PhotoBoardSelect";
import PhotoBoardReadonly from "../../../components/const/Photo/PhotoBoardReadonly";
import PeopleCell from "../../../components/common/people/PeopleCell";

import { getMaterialDoc } from "../../../api/material/meta";

export default function Request_new() {
  const { id: projectId, dns: paramDns } = useParams();
  const [dns, setDns] = useState(paramDns || "");

  /* ===============================
     HEADER STATE
  =============================== */
  const [title, setTitle] = useState("자재 검수 요청서");
  const [requestTitle, setRequestTitle] = useState("");
  const [requestDate, setRequestDate] = useState("");
  const [writer, setWriter] = useState("");
  const [requestPeople, setRequestPeople] = useState([]);

  /* ===============================
     PHOTOBOARD
  =============================== */
  const [photoboard, setPhotoboard] = useState(null);
  const [openPhotoModal, setOpenPhotoModal] = useState(false);

  /* ===============================
     ROWS
  =============================== */
  const makeRow = () => ({
    _id: uuid(),
    code: "",
    name: "",
    spec: "",
    unit: "EA",
    qty: 0,
    maker: "",
    remark: "",
  });

  const [rows, setRows] = useState(() =>
    Array.from({ length: 8 }, () => makeRow())
  );

  /* ===============================
     WRITER AUTO
  =============================== */
  useEffect(() => {
    const u = JSON.parse(localStorage.getItem("user") || "{}");
    setWriter(u.name || "");
  }, []);

  /* ===============================
     LOAD EXISTING DOC
  =============================== */
  useEffect(() => {
    if (!paramDns) return;

    (async () => {
      try {
        const res = await getMaterialDoc({
          dns: paramDns,
          project: projectId,
        });

        console.log("📄 Request_new 문서 로딩:", res);

        // 🔥 material 표준: header / lines
        const header = res.header || {};
        const lines = res.lines || [];
        const isRejected = res?.status === "rejected";

        // ⭐ 반려된 문서인 경우 dns를 설정하지 않음 (새 문서로 생성)
        // 일반 문서인 경우에만 dns를 설정 (기존 문서 수정)
        if (!isRejected) {
          setDns(paramDns);
        } else {
          console.log("🔄 반려 문서 복사 모드: 새 문서로 생성됩니다");
          setDns(""); // ✅ 명시적으로 빈 문자열 설정
        }

        setTitle(res.title || "자재 검수 요청서");
        setRequestDate(header.request_date || "");
        setWriter(header.writer || "");
        setRequestTitle(header.request_title || "");
        setPhotoboard(header.photoboard || null);

        // 담당자 정보 로드 (반려되지 않은 경우에만)
        if (!isRejected && Array.isArray(header.assigned_people)) {
          setRequestPeople(header.assigned_people.map((id) => ({ id })));
        } else if (isRejected) {
          // 반려된 문서는 담당자 초기화 (새로 지정해야 함)
          setRequestPeople([]);
        }

        if (Array.isArray(lines) && lines.length > 0) {
          setRows(lines.map((l) => ({ _id: uuid(), ...l })));
        }
      } catch (e) {
        console.error("문서 로딩 에러:", e);
        // ⚠️ 에러 발생 시에도 새 문서 모드로 동작
        setDns("");
        alert(
          `문서를 불러올 수 없어 새 문서 작성 모드로 전환합니다.\n에러: ${e.message}`
        );
      }
    })();
  }, [paramDns, projectId]);

  /* ===============================
     ROW HANDLERS
  =============================== */
  const updateCell = (id, key, value) =>
    setRows((prev) =>
      prev.map((r) => (r._id === id ? { ...r, [key]: value } : r))
    );

  const addRow = () => setRows((prev) => [...prev, makeRow()]);
  const removeRow = (id) =>
    setRows((prev) =>
      prev.length > 1 ? prev.filter((r) => r._id !== id) : prev
    );

  const totalQty = useMemo(
    () => rows.reduce((s, r) => s + Number(r.qty || 0), 0),
    [rows]
  );

  /* ===============================
     SUMMARY
  =============================== */
  const peopleLabel = useMemo(() => {
    if (!requestPeople.length) return "-";
    return requestPeople.map((p) => p.name || p.label || p.id).join(", ");
  }, [requestPeople]);

  const summaryConfig = {
    title: "자재 검수 요청서 요약",
    sections: [
      { label: "작성일자", value: requestDate || "-" },
      { label: "작성자", value: writer || "-" },
      { label: "담당자", value: peopleLabel },
      { label: "사진대지", value: photoboard || "-" },
      { label: "총 수량", value: totalQty },
    ],
  };

  /* ===============================
     🔥 SAVE BODY (표준 material QC)
  =============================== */
  const getBody = () => {
    const cleanLines = rows
      .filter((r) => Object.values(r).some((v) => v && v !== 0))
      .map(({ _id, ...rest }) => rest);

    const payload = {
      project: projectId,
      module: "material",
      doc_type: "QC",
      title,
      header: {
        request_date: requestDate || null,
        writer,
        request_title: requestTitle || "",
        photoboard,
        assigned_people: requestPeople.map((p) => p.id),
      },
      lines: cleanLines,
      level2: "GEN",
    };

    console.group("🧪 [Request_new] getBody payload");
    console.log(payload);
    console.groupEnd();

    return { values: payload };
  };

  /* ===============================
     RENDER
  =============================== */
  return (
    <FormContentManager
      params={{
        module: "material",
        project: projectId,
        dns,
      }}
      getBody={getBody}
      onChangeId={setDns}
      requestPeople={requestPeople}
      redirectTo={`/project/${projectId}/material/materialboard`}
      docName="자재 검수 요청서"
      docDescribe="자재 검수 요청 문서를 작성하세요."
      summaryConfig={summaryConfig}
    >
      {/* 반려 문서 복사 안내 */}
      {paramDns && !dns && (
        <div className="mb-4 p-4 bg-blue-50 border-2 border-blue-300 rounded-lg">
          <h3 className="text-sm font-bold text-blue-800 mb-1">
            📋 반려 문서 복사 모드
          </h3>
          <p className="text-xs text-blue-700">
            반려된 원본 문서는 그대로 유지되며, 수정 후 저장하면{" "}
            <strong>새로운 문서</strong>로 생성됩니다.
          </p>
          <p className="text-xs text-blue-600 mt-1">
            새 문서는 초안 상태로 저장되며, 담당자가 초기화됩니다.
          </p>
        </div>
      )}

      <div className="text-center mb-6">
        <div className="text-xl font-bold">{title}</div>
      </div>

      {/* HEADER */}
      <table className="doc-table w-full text-sm table-fixed mb-4">
        <tbody>
          <tr>
            <TableCell
              label="작성자"
              editing
              input={
                <input
                  readOnly
                  className="input-base bg-gray-100"
                  value={writer}
                />
              }
            />
            <TableCell
              label="작성일자"
              editing
              input={
                <input
                  type="date"
                  className="input-base"
                  value={requestDate}
                  onChange={(e) => setRequestDate(e.target.value)}
                />
              }
            />
            <TableCell
              label="담당자"
              editing
              input={
                <PeopleCell
                  value={requestPeople}
                  onChange={setRequestPeople}
                  single
                />
              }
            />
          </tr>

          <tr>
            <TableCell
              label="제목"
              colSpan={5}
              editing
              input={
                <input
                  className="input-base"
                  value={requestTitle}
                  onChange={(e) => setRequestTitle(e.target.value)}
                />
              }
            />
          </tr>
        </tbody>
      </table>

      {/* ITEM TABLE */}
      <div className="flex justify-between mb-2">
        <span className="text-body-bold">검수 항목</span>
        <button className="btn-basic-sm" onClick={addRow}>
          + 항목 추가
        </button>
      </div>

      <table className="doc-table w-full text-sm table-fixed">
        <thead className="doc-thead">
          <tr>
            <th className="doc-th w-20">자재코드</th>
            <th className="doc-th">자재명</th>
            <th className="doc-th">규격</th>
            <th className="doc-th w-14">단위</th>
            <th className="doc-th w-16">수량</th>
            <th className="doc-th w-24">제조회사명</th>
            <th className="doc-th">비고</th>
            <th className="doc-th w-12"></th>
          </tr>
        </thead>

        <tbody>
          {rows.map((r) => (
            <tr key={r._id}>
              <td className="doc-td">
                <input
                  className="input-sm"
                  value={r.code}
                  onChange={(e) => updateCell(r._id, "code", e.target.value)}
                />
              </td>
              <td className="doc-td">
                <input
                  className="input-sm"
                  value={r.name}
                  onChange={(e) => updateCell(r._id, "name", e.target.value)}
                />
              </td>
              <td className="doc-td">
                <input
                  className="input-sm"
                  value={r.spec}
                  onChange={(e) => updateCell(r._id, "spec", e.target.value)}
                />
              </td>
              <td className="doc-td text-center">
                <input
                  className="input-sm text-center"
                  value={r.unit}
                  onChange={(e) => updateCell(r._id, "unit", e.target.value)}
                />
              </td>
              <td className="doc-td text-right">
                <input
                  type="number"
                  className="input-sm text-right"
                  value={r.qty}
                  onChange={(e) => updateCell(r._id, "qty", e.target.value)}
                />
              </td>
              <td className="doc-td">
                <input
                  className="input-sm"
                  value={r.maker}
                  onChange={(e) => updateCell(r._id, "maker", e.target.value)}
                />
              </td>
              <td className="doc-td">
                <input
                  className="input-sm"
                  value={r.remark}
                  onChange={(e) => updateCell(r._id, "remark", e.target.value)}
                />
              </td>
              <td className="doc-td text-center">
                <button className="doc-delete" onClick={() => removeRow(r._id)}>
                  삭제
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* PHOTOBOARD */}
      <div className="mt-4 border rounded-md p-3">
        <div className="text-body-bold mb-2">사진대지</div>

        {photoboard ? (
          <>
            <PhotoBoardReadonly photoboardId={photoboard} />
            <button
              className="btn-basic-sm mt-3"
              onClick={() => setOpenPhotoModal(true)}
            >
              사진대지 변경
            </button>
          </>
        ) : (
          <>
            <div className="text-muted mb-2">등록된 사진대지가 없습니다.</div>
            <button
              className="btn-basic-sm"
              onClick={() => setOpenPhotoModal(true)}
            >
              사진대지 선택
            </button>
          </>
        )}
      </div>

      <PhotoBoardSelect
        isOpen={openPhotoModal}
        onClose={() => setOpenPhotoModal(false)}
        onSelect={(board) => {
          setPhotoboard(board.id);
          setOpenPhotoModal(false);
        }}
      />
    </FormContentManager>
  );
}
